const initializedBanners = new WeakSet<HTMLElement>()

/**
 * Return the vertical space an element currently occupies in the viewport.
 *
 * Viewport coverage is used instead of the element's intersection ratio
 * because the production footer can be taller than the viewport.
 */
function visibleHeight(
  rect: Pick<DOMRect, 'top' | 'bottom'>,
  viewportHeight: number,
): number {
  const visibleTop = Math.max(rect.top, 0)
  const visibleBottom = Math.min(rect.bottom, viewportHeight)
  return Math.max(visibleBottom - visibleTop, 0)
}

/**
 * Reveal the Best Books promotion after the page leaves its initial position,
 * then hide it when the production footer occupies more than half the viewport.
 *
 * The initializer is intentionally limited to the marked static banner and is
 * idempotent so the reduced public entry point can safely call it once the DOM
 * is ready. Scroll and resize updates use the banner's visible modifier class;
 * CSS owns the transition and reduced-motion behavior.
 */
export function initializeBestBooksFixedBanner(
  root: ParentNode = document,
): void {
  const banner = root.querySelector<HTMLElement>(
    '[data-best-books-fixed-banner]',
  )
  if (!banner || initializedBanners.has(banner)) {
    return
  }
  initializedBanners.add(banner)

  const page = banner.closest<HTMLElement>('[data-best-books-page-type]')
  const footer = page?.querySelector<HTMLElement>('footer.footer') ?? null

  const updateVisibility = (): void => {
    const footerCoversMostOfViewport =
      footer !== null &&
      visibleHeight(footer.getBoundingClientRect(), window.innerHeight) >
        window.innerHeight / 2

    banner.classList.toggle(
      'best-books-fixed-banner--visible',
      window.scrollY > 0 && !footerCoversMostOfViewport,
    )
  }

  window.addEventListener('scroll', updateVisibility, { passive: true })
  window.addEventListener('resize', updateVisibility)
  updateVisibility()
}
